import { useQuery } from "@tanstack/react-query";
import { BarChart3, CornerDownLeft, PencilRuler, Plus, Search, type LucideIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { designerUrl, listReportsFor, reportUrl, type Mode } from "./role";

interface Entry {
  id: string;
  section: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  run: () => void;
}

function score(q: string, text: string): number {
  if (!q) return 1;
  const t = text.toLowerCase();
  const s = q.toLowerCase().trim();
  if (t.startsWith(s)) return 3;
  if (t.includes(s)) return 2;
  // loose subsequence match ("mop perf" -> "MOP Performance")
  let i = 0;
  for (const ch of t) if (ch === s[i]) i++;
  return i === s.length ? 1 : 0;
}

export function CommandPalette({
  open,
  onClose,
  mode,
  navGroups,
}: {
  open: boolean;
  onClose: () => void;
  mode: Mode;
  navGroups: Array<{ label: string; items: Array<{ to: string; label: string; icon: LucideIcon }> }>;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const reports = useQuery({ queryKey: ["palette-reports", mode], queryFn: () => listReportsFor(mode), enabled: open, staleTime: 30_000 });

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const entries = useMemo<Entry[]>(() => {
    const go = (to: string) => () => {
      onClose();
      navigate(to);
    };
    const list: Entry[] = [];
    for (const r of reports.data ?? []) {
      if (r.status === "deleted") continue;
      list.push({ id: `r-${r.id}`, section: "Reports", label: r.name, hint: mode === "viewer" ? "Open" : "Open report", icon: BarChart3, run: go(reportUrl(mode, r.id)) });
      if (mode !== "viewer") list.push({ id: `d-${r.id}`, section: "Design", label: `Edit “${r.name}”`, hint: "Designer", icon: PencilRuler, run: go(designerUrl(mode, r.id)) });
    }
    if (mode === "admin") list.push({ id: "new", section: "Actions", label: "Create a new report", icon: Plus, run: go("/admin/reports?new=1") });
    for (const g of navGroups) for (const it of g.items) list.push({ id: `n-${it.to}`, section: "Go to", label: it.label, hint: g.label, icon: it.icon, run: go(it.to) });
    return list;
  }, [reports.data, mode, navGroups, navigate, onClose]);

  const results = useMemo(() => {
    const scored = entries.map((e) => ({ e, s: score(q, e.label) })).filter((x) => x.s > 0);
    scored.sort((a, b) => b.s - a.s);
    const limited = q ? scored.slice(0, 30) : scored.filter((x) => x.e.section !== "Design").slice(0, 14);
    return limited.map((x) => x.e);
  }, [entries, q]);

  useEffect(() => setActive(0), [q]);

  if (!open) return null;

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") onClose();
    else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      results[active]?.run();
    }
  };

  let lastSection = "";
  return createPortal(
    <div className="ds-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()} style={{ alignItems: "flex-start" }}>
      <div className="ds-palette" role="dialog" aria-label="Search" onKeyDown={onKey}>
        <div className="ds-palette__input">
          <Search size={18} className="ds-faint" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reports, pages and actions…" aria-label="Search" />
          <span className="ds-kbd">Esc</span>
        </div>
        <div className="ds-palette__list">
          {results.length === 0 ? <div className="ds-empty" style={{ padding: 28 }}>No matches for “{q}”.</div> : null}
          {results.map((r, i) => {
            const header = r.section !== lastSection ? r.section : null;
            lastSection = r.section;
            return (
              <div key={r.id}>
                {header ? <div className="ds-palette__section">{header}</div> : null}
                <button className={`ds-palette__item${i === active ? " is-active" : ""}`} onMouseEnter={() => setActive(i)} onClick={r.run}>
                  <r.icon size={16} className="ds-faint" />
                  <span className="ds-grow ds-truncate">{r.label}</span>
                  {r.hint ? <small>{r.hint}</small> : null}
                  {i === active ? <CornerDownLeft size={14} className="ds-faint" /> : null}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
